import { useState } from 'react';
import { useSelector } from 'react-redux';

import {
  CareEnvironment,
  CarePhase,
  careEnvironmentDefs,
  carePhaseDefs,
  carePhaseDefList,
  pickClientCareEnvironmentCosts,
} from '@shared';
import { selectClient } from '@/model';

export interface CarePhasesSummaryData {
  id: number;
  phaseLabel: string;
  phaseInflatedProfessionalShareCost: number;
  careEnvironment: CareEnvironment;
  careEnvironmentLabel: string;
  rateAmount: number;
  hasCustomRate: boolean;
  rateLocaleDescription: string;
  phaseCareMonthsNeeded: number;
  phaseProfessionalCareHoursProvided: number;
  phaseInflationFactor: number | null;
  inHomeEnvironment: boolean;
  yearsTillPhaseEnd: number | null;
  isDurationCustom: boolean;
}

export function useCarePhasesSummarySlide() {
  const client = useSelector(selectClient)!;

  const {
    phaseCosts,
    appliedCareEnvironments,
    clientPhaseInflationFactors,
    clientYearsTillPhaseEnd,
    allPhaseCosts: { allPhaseInflatedProfessionalShareCost },
    clientCalculationSettings: { healthCareInflationRate },
  } = client;

  const [openEditCostModal, setOpenEditCostModal] = useState(false);
  const [openEditInflationModal, setOpenEditInflationModal] = useState(false);
  const [selectedCareEnvironment, setSelectedCareEnvironment] = useState<CareEnvironment | null>(
    null,
  );

  const handleEditCostClick = (careEnvironment: CareEnvironment) => {
    setSelectedCareEnvironment(careEnvironment);
    setOpenEditCostModal(true);
  };

  const handleEditInflationClick = () => {
    setOpenEditInflationModal(true);
  };

  const carePhaseData: CarePhasesSummaryData[] = carePhaseDefList.map(phase => {
    const singlePhaseCosts = phaseCosts[phase.index];
    const phaseLabel = carePhaseDefs[phase.value].label;
    const yearsTillPhaseEnd = clientYearsTillPhaseEnd[phase.value];
    const {
      inHomeEnvironment,
      phaseInflatedProfessionalShareCost,
      phaseCareMonthsNeeded,
      phaseProfessionalCareHoursProvided,
    } = singlePhaseCosts;
    const phaseInflationFactor = clientPhaseInflationFactors[phase.value];
    const careEnvironment = appliedCareEnvironments[phase.value];
    const careEnvironmentLabel = careEnvironmentDefs[careEnvironment].label;
    const isDurationCustom = singlePhaseCosts.isDurationCustom;
    const { rateAmount, hasCustomRate, rateLocaleDescription } = pickClientCareEnvironmentCosts(
      client,
      careEnvironment,
    );

    return {
      id: phase.index,
      phaseLabel,
      phaseInflatedProfessionalShareCost,
      careEnvironment,
      careEnvironmentLabel,
      rateAmount,
      hasCustomRate,
      rateLocaleDescription,
      phaseCareMonthsNeeded,
      phaseProfessionalCareHoursProvided,
      phaseInflationFactor,
      inHomeEnvironment,
      yearsTillPhaseEnd,
      isDurationCustom,
    };
  });

  const visibleCarePhaseData = carePhaseData.filter(phase => Boolean(phase.yearsTillPhaseEnd));

  // For useSlideSetup previousSlidePage computation
  const isFullCareHome = appliedCareEnvironments[CarePhase.fullCare] === CareEnvironment.home;

  return {
    client,
    allPhaseInflatedProfessionalShareCost,
    healthCareInflationRate,
    visibleCarePhaseData,
    isFullCareHome,
    openEditCostModal,
    setOpenEditCostModal,
    openEditInflationModal,
    setOpenEditInflationModal,
    selectedCareEnvironment,
    handleEditCostClick,
    handleEditInflationClick,
  };
}
